import type {
  GetTransportSchemeResponse,
  SaveTransportSchemeRequest,
  TransportMode,
  TransportSchemeTicketResponse,
  TransportMatrixRowResponse,
} from '@/api/generated/schemas';
import { TransportSchemeKind, TransportSchemeTicketKind } from '@/api/generated/schemas';

/** Rozsah, ve kterém se záložka renderuje — seriál (sdílená definice) nebo jeden termín. */
export type TransportSchemeScope = 'serie' | 'term';

/**
 * Jakou dopravou se jede daným směrem. Zrcadlí `TransportSchemeMap.ModeOf` na serveru — klient to musí
 * umět i sám, protože přepnutí kombinované varianty je až do uložení jen v draftu a panely se musí
 * překreslit hned.
 */
export function modeOf(
  kind: GetTransportSchemeResponse['kind'],
  direction: 'Outbound' | 'Inbound'
): TransportMode {
  if (kind === TransportSchemeKind.Air) return 'Air';
  if (kind === TransportSchemeKind.Bus) return 'Bus';
  const isAir = (kind === TransportSchemeKind.AirBus) === (direction === 'Outbound');
  return isAir ? 'Air' : 'Bus';
}

/**
 * Neuložený stav záložky. Řádky panelů se skládají **z draftu**, ne ze serverové odpovědi — jinak by
 * po přidání dopravy bliklo staré zobrazení, než dorazí uložení.
 */
export interface TransportSchemeDraft {
  kind: GetTransportSchemeResponse['kind'];
  costCurrency: string | null;
  /** Kapacita seriálu z řádku „Společné hodnoty" — termíny ji dědí, dokud nemají vlastní. */
  sharedCapacity: number | null;
  /** Tickety včetně sekvencí; jejich pořadí i počet určuje výhradně server (endpoint `chain`). */
  tickets: TransportSchemeTicketResponse[];
  /** Hodnoty jednotlivých termínů — kapacita, ruční ceny, přiřazené linky. */
  rows: TransportMatrixRowResponse[];
  outbound: DirectionDraft;
  inbound: DirectionDraft;
  termDirections: TermDirectionDraft[];
}

/** Autobusová strana směru — u letecké se needituje nic. */
export interface DirectionDraft {
  stationId: number | null;
  stationName: string | null;
  timeMinutes: number | null;
}

/** Přiřazená trasa jednoho termínu pro jeden směr. */
export interface TermDirectionDraft {
  termId: string;
  direction: 'Outbound' | 'Inbound';
  stationChainId: string | null;
  stationChainName: string | null;
  stationChainReversed: boolean;
}

/** Tickety, které vstupují do součtu „Celkem" — spojený let je příplatek a nepočítá se. */
export function isTotalTicket(ticket: TransportSchemeTicketResponse): boolean {
  return (
    ticket.kind === TransportSchemeTicketKind.Primary ||
    ticket.kind === TransportSchemeTicketKind.Bus
  );
}

/**
 * Je cena nákupní jednotky **za celou skupinu** místo za osobu?
 *
 * Autobusová strana je jeden nájem za oba směry, takže se její částka neškáluje počtem osob a jede
 * slotem `group`; letenka i spojený let se kupují na osobu a jedou slotem `pax`. Zrcadlí serverový
 * `TransportSchemeMap.IsGroupPriced`, který je při ukládání autoritou — klient podle toho jen ví, který
 * slot číst a psát, aby se hodnota neminula.
 */
export function isGroupPricedTicket(ticket: TransportSchemeTicketResponse): boolean {
  return ticket.kind === TransportSchemeTicketKind.Bus;
}

/** Cena z definice schématu (ticketu) ze slotu, který danému druhu patří. */
export function definitionAmountOf(ticket: TransportSchemeTicketResponse): number | null {
  const amount = isGroupPricedTicket(ticket)
    ? ticket.expectedGroupAmount
    : ticket.expectedPaxAmount;
  return amount ?? null;
}

/** Efektivní cena termínu (zděděná nebo ručně přepsaná) ze slotu, který danému druhu patří. */
export function costAmountOf(
  cost: { paxAmount?: number | null; groupAmount?: number | null } | undefined,
  ticket: TransportSchemeTicketResponse
): number | null {
  if (!cost) return null;
  const amount = isGroupPricedTicket(ticket) ? cost.groupAmount : cost.paxAmount;
  return amount ?? null;
}

/** Zápis ceny do slotu podle druhu ticketu — druhý slot se nuluje, ať se částka nepočítá dvakrát. */
export function withAmountForKind<
  T extends { paxAmount?: number | null; groupAmount?: number | null },
>(target: T, ticket: TransportSchemeTicketResponse, value: number | null): T {
  return isGroupPricedTicket(ticket)
    ? { ...target, paxAmount: null, groupAmount: value }
    : { ...target, paxAmount: value, groupAmount: null };
}

/** Převod draftu na tělo uložení. */
export function toSaveRequest(draft: TransportSchemeDraft): SaveTransportSchemeRequest {
  return {
    kind: draft.kind,
    costCurrency: draft.costCurrency,
    sharedCapacity: draft.sharedCapacity,
    directions: [
      {
        direction: 'Outbound',
        stationId: draft.outbound.stationId,
        timeMinutes: draft.outbound.timeMinutes,
      },
      {
        direction: 'Inbound',
        stationId: draft.inbound.stationId,
        timeMinutes: draft.inbound.timeMinutes,
      },
    ],
    tickets: draft.tickets.map((ticket) => ({
      ticketId: ticket.ticketId,
      kind: ticket.kind,
      regularTransportId: ticket.regularTransportId,
      outboundSequence: ticket.outboundSequence,
      inboundSequence: ticket.inboundSequence,
      expectedPaxAmount: ticket.expectedPaxAmount,
      expectedGroupAmount: ticket.expectedGroupAmount,
    })),
    terms: draft.rows.map((row) => ({
      termId: row.termId,
      isOwnCapacity: row.isOwnCapacity,
      capacity: row.capacity,
      costs: row.costs.map((cost) => ({
        ticketId: cost.ticketId,
        isManual: cost.isManual,
        paxAmount: cost.paxAmount,
        groupAmount: cost.groupAmount,
      })),
      directions: draft.termDirections
        .filter((d) => d.termId === row.termId)
        .map((d) => ({
          direction: d.direction,
          stationChainId: d.stationChainId,
          stationChainReversed: d.stationChainReversed,
        })),
    })),
  };
}

/** Vytvoří draft z odpovědi serveru. */
export function toDraft(response: GetTransportSchemeResponse): TransportSchemeDraft {
  return {
    kind: response.kind,
    costCurrency: response.costCurrency ?? null,
    sharedCapacity: response.sharedCapacity,
    tickets: response.tickets,
    rows: response.rows,
    outbound: {
      stationId: response.outbound.stationId ?? null,
      stationName: response.outbound.stationName ?? null,
      timeMinutes: response.outbound.timeMinutes ?? null,
    },
    inbound: {
      stationId: response.inbound.stationId ?? null,
      stationName: response.inbound.stationName ?? null,
      timeMinutes: response.inbound.timeMinutes ?? null,
    },
    // Trasa patří termínu, ne definici — proto jede na řádcích matice, ne na směrech schématu.
    termDirections: response.rows.flatMap((row) =>
      row.directions.map((direction) => ({
        termId: row.termId,
        direction: direction.direction,
        stationChainId: direction.stationChainId ?? null,
        stationChainName: direction.stationChainName ?? null,
        stationChainReversed: direction.stationChainReversed,
      }))
    ),
  };
}
